import { useCallback, useEffect, useState } from 'react';
import type { CodigoWidget, ListaVistos, Tema, TipoWidget, Widget } from '../../api/ops/web';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Boton } from '../../componentes/Boton';
import { Campo } from '../../componentes/Campo';
import { Chip } from '../../componentes/Chip';
import { Deshacer } from '../../componentes/Deshacer';
import { Desplegable } from '../../componentes/Desplegable';
import { EstadoCargando, EstadoError, EstadoVacia } from '../../componentes/EstadoPagina';
import { Filtros } from '../../componentes/Filtros';
import { Icono } from '../../componentes/Icono';
import { PanelLateral } from '../../componentes/PanelLateral';
import { Segmentos } from '../../componentes/Segmentos';
import { Tabla } from '../../componentes/Tabla';
import { Ventana } from '../../componentes/Ventana';
import { useEnvioRetrasado } from '../../logica/envioRetrasado';
import { avisoBreveDeError, estadoDeError } from '../../logica/estados';
import { cifra, diaMes, diaYHora } from '../../logica/fechasCortas';
import { navegador } from '../../logica/navegador';
import { useCabecera } from '../../marco/cabecera';
import { usePermisos } from '../../permisos/permisos';
import type { PropsPantalla } from '../tipos';
import { ParaDesarrolladores } from './ClavesApi';
import { EditorOpciones } from './Opciones';
import { chipWidget, dominiosDe, hayOpciones, FILTROS_WIDGETS, MOTIVO_BLOQUEO, MOTIVO_PLEGADO, motivoRetirada, rangoDe } from './reglas';
import { AYUDA_WIDGETS, CATALOGO_PROVISIONAL } from './textos';

/**
 * «Widgets» (13.4): tabla (en móvil, tarjetas) con nombre, tipo, dónde se puede ver, últimos 7 días (solo con
 * `web.medicion.detalle`, G616) y estado; filtro sin cifras. Ficha en RV Panel con Ajustes · Vista previa · Código ·
 * Dónde se ve. Pausar y reactivar con «Deshacer» 5 s (G159); al reactivar, si sigue plegado se dice por qué (G579).
 * Retirado por abuso: el motivo de `active_takedown` (G580, G612) y no se puede reactivar ni cambiar. Dominios sin
 * `https://` (G125). `canales.whatsapp` sin WhatsApp conectado se pliega con `module_inactive` (G619). Al final, plegado,
 * «Para desarrolladores» (13.6, `ClavesApi.tsx`) con `api.claves.gestionar`; `?apartado=api` lo abre (G635).
 */
const TEMAS: readonly { valor: Tema; texto: string; icono: 'sun' | 'moon' | 'monitor'; ayuda: string }[] = [
    { valor: 'light', texto: 'Claro', icono: 'sun', ayuda: 'Fondo blanco, para la mayoría de webs.' },
    { valor: 'dark', texto: 'Oscuro', icono: 'moon', ayuda: 'Fondo oscuro, para webs oscuras.' },
    { valor: 'auto', texto: 'Automático', icono: 'monitor', ayuda: 'Sigue el modo del móvil o del ordenador de quien visita.' },
];
const IDIOMAS = [
    { valor: '', texto: 'El de quien visita' },
    { valor: 'es-ES', texto: 'Español' },
    { valor: 'en', texto: 'English' },
];
const COLOR = /^#[0-9A-Fa-f]{6}$/;
const ACENTO_INICIAL = '#DD1420';

interface Datos {
    widgets: Widget[];
    tipos: TipoWidget[];
    semana: Record<string, { impresiones: number; clics: number; ventas: number }> | null;
}

export function Widgets(props: PropsPantalla) {
    const { ops, negocio, movil, soloLectura, busqueda, navegar, ruta } = props;
    const { puede } = usePermisos();
    const detalle = puede('web.medicion.detalle');
    const avisar = useAvisoBreve();
    const [datos, setDatos] = useState<Datos | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [filtro, setFiltro] = useState(0);
    const [nuevo, setNuevo] = useState(false);
    const abierto = busqueda.get('widget');
    const recargar = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);

    useEffect(() => {
        let vivo = true;
        const semana = rangoDe('7');
        Promise.all([
            ops.listWidgets(negocio, { limit: 100 }),
            ops.listWidgetTypes(negocio),
            detalle ? ops.getWebMetrics(negocio, { from: semana.actual.from, to: semana.actual.to, breakdown: 'widget', granularity: 'week' }).catch(() => null) : Promise.resolve(null),
        ])
            .then(([w, t, m]) => {
                if (!vivo) {
                    return;
                }
                let porWidget: Datos['semana'] = null;
                if (m !== null) {
                    porWidget = {};
                    for (const p of m.data) {
                        if (p.widget_id == null) {
                            continue;
                        }
                        const a = porWidget[p.widget_id] ?? { impresiones: 0, clics: 0, ventas: 0 };
                        porWidget[p.widget_id] = { impresiones: a.impresiones + p.impressions, clics: a.clics + p.clicks, ventas: a.ventas + p.net_conversions };
                    }
                }
                setDatos({ widgets: w.data.filter((x) => x.status !== 'deleted'), tipos: t.data, semana: porWidget });
                setError(null);
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, detalle, intento]);

    useCabecera({ ayuda: AYUDA_WIDGETS, acciones: soloLectura || datos === null ? [] : [{ clave: 'nuevo', texto: 'Nuevo widget', icono: 'plus', onClick: () => { setNuevo(true); } }] });

    if (error !== null) {
        return <EstadoError seccion="los widgets" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={recargar} movil={movil} />;
    }
    if (datos === null) {
        return <EstadoCargando seccion="los widgets" />;
    }
    const base = ruta.split('?')[0] ?? ruta;
    const abrir = (id: string | null) => { navegar(id === null ? `${base}?apartado=widgets` : `${base}?apartado=widgets&widget=${encodeURIComponent(id)}`, true); };
    const estados = FILTROS_WIDGETS[filtro]?.estados ?? null;
    const visibles = datos.widgets.filter((w) => estados === null || estados.includes(w.status));
    const retirado = datos.widgets.find((w) => w.active_takedown != null) ?? null;
    const nombreTipo = (codigo: string) => datos.tipos.find((t) => t.code === codigo)?.name ?? codigo;
    const elegido = datos.widgets.find((w) => w.id === abierto) ?? null;

    return (
        <div className="rv-ca-apartado">
            {retirado?.active_takedown != null && (
                <p className="rv-wb-banda rv-wb-banda--roja" role="alert">
                    <Icono nombre="ban" />
                    <span>
                        <strong>Widget retirado por abuso.</strong> {`Reservaria ha retirado «${retirado.name}». Motivo: ${motivoRetirada(retirado.active_takedown)}. Te lo hemos avisado también en la campana.`}
                    </span>
                </p>
            )}
            {datos.widgets.length === 0 ? (
                <EstadoVacia seccion="Widgets" />
            ) : (
                <>
                    <Filtros etiqueta="Estado" items={FILTROS_WIDGETS.map((f) => ({ etiqueta: f.etiqueta }))} valor={filtro} onCambio={setFiltro} />
                    <Tabla
                        etiqueta="Widgets"
                        movil={movil}
                        columnas={[
                            { clave: 'widget', titulo: 'Widget', ancho: 'minmax(0, 2fr)' },
                            { clave: 'donde', titulo: 'Dónde se puede ver' },
                            ...(datos.semana === null ? [] : [{ clave: 'semana', titulo: 'Últimos 7 días' }]),
                            { clave: 'estado', titulo: 'Estado', ancho: '160px' },
                        ]}
                        filas={visibles.map((w) => {
                            const chip = chipWidget(w);
                            const s = datos.semana?.[w.id];
                            return {
                                clave: w.id,
                                etiqueta: w.name,
                                onAbrir: () => { abrir(w.id); },
                                celdas: {
                                    widget: (
                                        <span className="rv-en-celda">
                                            <strong>{w.name}</strong>
                                            <small>{`${w.module.charAt(0).toLocaleUpperCase('es')}${w.module.slice(1)} · ${nombreTipo(w.type_code)}`}</small>
                                        </span>
                                    ),
                                    donde: (
                                        <span className="rv-en-celda">
                                            <strong>{w.domains[0] ?? 'Sin dominios'}</strong>
                                            <small>{w.domains.length === 0 ? 'Añade una web para activarlo' : w.domains.length > 1 ? `y ${String(w.domains.length - 1)} más` : 'Solo esta web'}</small>
                                        </span>
                                    ),
                                    semana:
                                        s === undefined ? (
                                            <span className="rv-en-celda">
                                                <strong>Sin datos todavía</strong>
                                                <small>{w.status === 'draft' ? 'Se cuenta al activarlo' : '0 clics · 0 ventas'}</small>
                                            </span>
                                        ) : (
                                            <span className="rv-en-celda">
                                                <strong>{`${cifra(s.impresiones)} impresiones`}</strong>
                                                <small>{`${cifra(s.clics)} clics · ${cifra(s.ventas)} ventas`}</small>
                                            </span>
                                        ),
                                    estado: chip === null ? '' : <Chip estado={chip.estado}>{chip.texto}</Chip>,
                                },
                            };
                        })}
                        vacia={<span>No hay widgets con este estado.</span>}
                    />
                </>
            )}
            <p className="rv-wb-provisional">{`${CATALOGO_PROVISIONAL} No hay widget para apuntarse a una lista: se hace con el enlace de cada RRPP.`}</p>
            {puede('api.claves.gestionar') && <ParaDesarrolladores {...props} abrirAlEntrar={busqueda.get('apartado') === 'api'} />}
            {elegido !== null && (
                <Ficha
                    key={elegido.id}
                    {...props}
                    widget={elegido}
                    tipo={datos.tipos.find((t) => t.code === elegido.type_code)}
                    onCerrar={() => { abrir(null); }}
                    onCambiado={recargar}
                />
            )}
            <Nuevo
                {...props}
                abierto={nuevo}
                tipos={datos.tipos}
                onCerrar={() => { setNuevo(false); }}
                onCreado={(w) => {
                    setNuevo(false);
                    recargar();
                    abrir(w.id);
                    avisar({ tipo: 'ok', texto: w.status === 'draft' ? 'Widget guardado como borrador.' : 'Widget activado.' });
                }}
            />
        </div>
    );
}

/** Lo que dice la ficha de por qué no se enseña (G579). */
function lineaPlegado(w: Widget): string | null {
    // G619: el botón de WhatsApp se pliega con `module_inactive` cuando WhatsApp no está conectado en Canales.
    const motivos = w.fold_reasons.map((m) => (m === 'module_inactive' && w.type_code === 'canales.whatsapp' ? 'WhatsApp no está conectado en Canales' : MOTIVO_PLEGADO[m]));
    if (motivos.length === 0) {
        return null;
    }
    if (w.status === 'paused') {
        return `Ojo: aunque lo reactives seguirá plegado, porque ${motivos.join(' y ')}.`;
    }
    return `Plegado: no se enseña porque ${motivos.join(' y ')}. Cuando se resuelva, vuelve a salir solo.`;
}

type Pestana = 'ajustes' | 'vista' | 'codigo' | 'donde';
const PESTANAS: readonly { valor: Pestana; texto: string; icono: 'sliders-horizontal' | 'eye' | 'code' | 'globe' }[] = [
    { valor: 'ajustes', texto: 'Ajustes', icono: 'sliders-horizontal' },
    { valor: 'vista', texto: 'Vista previa', icono: 'eye' },
    { valor: 'codigo', texto: 'Código', icono: 'code' },
    { valor: 'donde', texto: 'Dónde se ve', icono: 'globe' },
];

function Ficha({ ops, negocio, movil, soloLectura, widget, tipo, onCerrar, onCambiado }: PropsPantalla & { widget: Widget; tipo: TipoWidget | undefined; onCerrar: () => void; onCambiado: () => void }) {
    const { puede } = usePermisos();
    const avisar = useAvisoBreve();
    const [pestana, setPestana] = useState<Pestana>('ajustes');
    const [actual, setActual] = useState<{ widget: Widget; version: string } | null>(null);
    const [copia, setCopia] = useState<{ name: string; options: Record<string, unknown>; theme: Tema; accent_color: string; locale: string; dominios: string }>({ name: widget.name, options: widget.options, theme: widget.theme, accent_color: widget.accent_color, locale: widget.locale ?? '', dominios: widget.domains.join('\n') });
    const [ocupado, setOcupado] = useState<string | null>(null);
    const [codigo, setCodigo] = useState<CodigoWidget | null>(null);
    const [vistos, setVistos] = useState<ListaVistos | null>(null);
    const [borrar, setBorrar] = useState(false);
    const [aviso, setAviso] = useState<string | null>(null);
    const retraso = useEnvioRetrasado();

    useEffect(() => {
        let vivo = true;
        ops.getWidget(negocio, widget.id)
            .then((r) => {
                if (vivo) {
                    setActual({ widget: r.datos, version: r.version ?? '' });
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [ops, negocio, widget.id]);

    useEffect(() => {
        let vivo = true;
        if (pestana === 'codigo' && codigo === null) {
            ops.getWidgetEmbedCode(negocio, widget.id)
                .then((c) => {
                    if (vivo) {
                        setCodigo(c);
                    }
                })
                .catch((e: unknown) => { avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido leer el código.' }); });
        }
        if (pestana === 'donde' && vistos === null && puede('web.medicion.detalle')) {
            ops.listWidgetSightings(negocio, widget.id, { limit: 50 })
                .then((v) => {
                    if (vivo) {
                        setVistos(v);
                    }
                })
                .catch(() => undefined);
        }
        return () => {
            vivo = false;
        };
    }, [pestana, codigo, vistos, ops, negocio, widget.id, puede, avisar]);

    const w = actual?.widget ?? widget;
    const retirado = w.active_takedown ?? null;
    const bloqueado = soloLectura || retirado !== null;
    const dominios = dominiosDe(copia.dominios);
    const malo = copia.name.trim() === '' || !COLOR.test(copia.accent_color) || dominios.length > 10;
    const plegado = lineaPlegado(w);
    const tema = TEMAS.find((t) => t.valor === copia.theme) ?? TEMAS[0];

    const guardar = () => {
        if (actual === null) {
            return;
        }
        setOcupado('guardar');
        ops.updateWidget(negocio, w.id, { name: copia.name.trim(), options: copia.options as Record<string, never>, theme: copia.theme, accent_color: copia.accent_color, locale: copia.locale === '' ? null : copia.locale, domains: dominios }, actual.version)
            .then((r) => {
                setActual({ widget: r.datos, version: r.version ?? '' });
                avisar({ tipo: 'ok', texto: 'Widget guardado.' });
                onCambiado();
            })
            .catch((e: unknown) => {
                avisar({ tipo: 'error', texto: e instanceof ErrorApi && e.codigo === 'version_conflict' ? 'Otra persona ha cambiado el widget. Ciérralo y ábrelo otra vez.' : (avisoBreveDeError(e) ?? 'No se ha podido guardar.') });
            })
            .finally(() => { setOcupado(null); });
    };

    const cambiarEstado = (pausar: boolean) => {
        retraso.programar({
            texto: pausar ? 'Widget pausado.' : 'Widget reactivado.',
            donde: 'ficha',
            enviar: () => {
                (pausar ? ops.pauseWidget(negocio, w.id) : ops.activateWidget(negocio, w.id))
                    .then((r) => {
                        setActual((a) => (a === null ? a : { ...a, widget: r }));
                        // G579: al reactivar, si sigue plegado se dice por qué.
                        setAviso(!pausar && r.status === 'folded' ? lineaPlegado(r) : null);
                        onCambiado();
                    })
                    .catch((e: unknown) => { avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido cambiar.' }); });
            },
        });
    };

    const vistaPrevia = () => {
        setOcupado('vista');
        ops.createWidgetPreview(negocio, { widget_id: w.id, type_code: w.type_code, options: copia.options as Record<string, never>, theme: copia.theme, accent_color: copia.accent_color, ...(copia.locale === '' ? {} : { locale: copia.locale }) })
            .then((v) => { navegador.abrir(v.url); })
            .catch((e: unknown) => { avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido preparar la vista previa.' }); })
            .finally(() => { setOcupado(null); });
    };

    const copiar = (texto: string) => {
        const portapapeles = navigator.clipboard as Clipboard | undefined;
        void portapapeles?.writeText(texto).then(() => { avisar({ tipo: 'ok', texto: 'Código copiado.' }); });
    };

    const chip = chipWidget(w);
    return (
        <>
            <PanelLateral
                abierto
                onCerrar={onCerrar}
                movil={movil}
                tamano="ficha"
                titulo={w.name}
                eyebrow={`Widget · ${tipo?.name ?? w.type_code}`}
                subtitulo={w.domains.join(', ')}
                chip={chip?.texto}
                chipEstado={chip?.estado}
                icono="code"
                accion={pestana === 'ajustes' && !bloqueado ? 'Guardar' : ''}
                ocupado={ocupado === 'guardar'}
                inactiva={malo || actual === null}
                onAccion={guardar}
            >
                <div className="rv-wb-form">
                    <Segmentos etiqueta="Partes del widget" movil={movil} items={PESTANAS.map((p) => ({ etiqueta: p.texto, icono: p.icono }))} valor={PESTANAS.findIndex((p) => p.valor === pestana)} onCambio={(i) => { setPestana(PESTANAS[i]?.valor ?? 'ajustes'); }} />
                    {retirado !== null && (
                        <p className="rv-wb-banda rv-wb-banda--roja" role="alert">
                            <Icono nombre="ban" />
                            <span>
                                <strong>Retirado por abuso.</strong> {`Motivo: ${motivoRetirada(retirado)}. No se puede reactivar ni cambiar.`}
                            </span>
                        </p>
                    )}
                    {pestana === 'ajustes' && (
                        <>
                            <div className="rv-wb-estado">
                                <span>
                                    {w.status === 'active'
                                        ? `Se ve en ${w.domains.join(', ')}${w.activated_at == null ? '' : ` desde el ${diaMes(w.activated_at)}`}.`
                                        : w.status === 'paused'
                                          ? 'Pausado: no se enseña en ninguna web.'
                                          : w.status === 'folded'
                                            ? 'Plegado: está activo, pero no se enseña.'
                                            : 'Borrador: aún no se enseña. Añade al menos una web para activarlo.'}
                                </span>
                                {!bloqueado &&
                                    (w.status === 'active' || w.status === 'folded' ? (
                                        <Boton tipo="secundario" icono="pause" onClick={() => { cambiarEstado(true); }}>
                                            Pausar
                                        </Boton>
                                    ) : (
                                        <Boton tipo="secundario" icono="play" disabled={w.domains.length === 0} onClick={() => { cambiarEstado(false); }}>
                                            {w.status === 'draft' ? 'Activar' : 'Reactivar'}
                                        </Boton>
                                    ))}
                            </div>
                            {retraso.pendiente !== null && (
                                <div className="rv-ca-deshacer">
                                    <span>{retraso.pendiente.texto}</span>
                                    <Deshacer clave={retraso.pendiente.clave} onDeshacer={() => { retraso.deshacer(); }} />
                                </div>
                            )}
                            {(aviso ?? plegado) !== null && retirado === null && (
                                <p className="rv-wb-banda">
                                    <Icono nombre="circle-alert" />
                                    <span>{aviso ?? plegado}</span>
                                </p>
                            )}
                            <Campo etiqueta="Nombre del widget" valor={copia.name} max={80} ayuda="Solo lo ve tu equipo." onChange={(v) => { setCopia({ ...copia, name: v }); }} bloqueado={bloqueado} />
                            <h3 className="rv-wb-ceja">Qué hace</h3>
                            <p className="rv-ca-nota">
                                <Icono nombre="layout-template" />
                                <span>
                                    <strong>{tipo?.name ?? w.type_code}</strong> · El tipo no se cambia: para otro, crea otro widget.
                                </span>
                            </p>
                            {tipo !== undefined && hayOpciones(tipo.options_schema) && <EditorOpciones esquema={tipo.options_schema} valor={copia.options} onChange={(o) => { setCopia({ ...copia, options: o }); }} bloqueado={bloqueado} />}
                            <h3 className="rv-wb-ceja">Cómo se ve</h3>
                            <Segmentos etiqueta="Tema" items={TEMAS.map((t) => ({ etiqueta: t.texto, icono: t.icono }))} valor={Math.max(0, TEMAS.findIndex((t) => t.valor === copia.theme))} onCambio={(i) => { if (!bloqueado) { setCopia({ ...copia, theme: TEMAS[i]?.valor ?? 'light' }); } }} />
                            <p className="rv-ca-nota rv-ca-nota--gris">{tema?.ayuda}</p>
                            <div className="rv-wb-color">
                                <i style={{ background: COLOR.test(copia.accent_color) ? copia.accent_color : 'transparent' }} aria-hidden="true" />
                                <Campo etiqueta="Color de acento" valor={copia.accent_color} max={7} ayuda="Botones y lo elegido." error={COLOR.test(copia.accent_color) ? null : 'Escríbelo así: #DD1420'} onChange={(v) => { setCopia({ ...copia, accent_color: v.trim() }); }} bloqueado={bloqueado} />
                            </div>
                            <Desplegable etiqueta="Idioma" valor={copia.locale} opciones={IDIOMAS} onChange={(v) => { setCopia({ ...copia, locale: v }); }} bloqueado={bloqueado} />
                            <h3 className="rv-wb-ceja">Dónde se puede ver</h3>
                            <Campo etiqueta="Webs donde lo pegas" filas={3} valor={copia.dominios} onChange={(v) => { setCopia({ ...copia, dominios: v }); }} ayuda="Una por línea, sin https:// (casetaembrujo.es). Hasta 10." error={dominios.length > 10 ? 'Como mucho 10 webs.' : null} bloqueado={bloqueado} />
                            {!bloqueado && (
                                <div>
                                    <Boton tipo="secundario" rojo icono="trash-2" onClick={() => { setBorrar(true); }}>
                                        Borrar el widget
                                    </Boton>
                                </div>
                            )}
                        </>
                    )}
                    {pestana === 'vista' && (
                        <div className="rv-wb-vista">
                            <span className="rv-ca-baldosa rv-wb-vista__baldosa">
                                <Icono nombre="eye" />
                            </span>
                            <p>Ábrela para verlo con lo que has cambiado, aunque aún no lo hayas guardado.</p>
                            <Boton tipo="secundario" icono="external-link" ocupado={ocupado === 'vista'} onClick={vistaPrevia}>
                                Ver la vista previa
                            </Boton>
                        </div>
                    )}
                    {pestana === 'codigo' &&
                        (codigo === null ? (
                            <EstadoCargando seccion="el código" />
                        ) : (
                            <>
                                <h3 className="rv-wb-ceja">Pega esto en tu web</h3>
                                <pre className="rv-wb-codigo">{codigo.script_snippet}</pre>
                                <div className="rv-wb-botones">
                                    <Boton tipo="secundario" icono="copy" onClick={() => { copiar(codigo.script_snippet); }}>
                                        Copiar el código
                                    </Boton>
                                </div>
                                <h3 className="rv-wb-ceja">Si tu web no deja pegar scripts</h3>
                                <pre className="rv-wb-codigo">{codigo.iframe_snippet}</pre>
                                <div className="rv-wb-botones">
                                    <Boton tipo="secundario" icono="copy" onClick={() => { copiar(codigo.iframe_snippet); }}>
                                        Copiar el marco
                                    </Boton>
                                </div>
                                {codigo.instructions.map((i) => (
                                    <details key={i.platform} className="rv-wb-pasos">
                                        <summary>{({ html: 'HTML', wordpress: 'WordPress', wix: 'Wix', shopify: 'Shopify' } as const)[i.platform]}</summary>
                                        <ol>
                                            {i.steps.map((p) => (
                                                <li key={p}>{p}</li>
                                            ))}
                                        </ol>
                                    </details>
                                ))}
                            </>
                        ))}
                    {pestana === 'donde' &&
                        (!puede('web.medicion.detalle') ? (
                            <p className="rv-ca-nota rv-ca-nota--gris">Con tu rol no ves dónde se ha visto cada widget.</p>
                        ) : vistos === null ? (
                            <EstadoCargando seccion="dónde se ve" />
                        ) : vistos.data.length === 0 ? (
                            <p className="rv-ca-nota rv-ca-nota--gris">Aún no se ha visto en ninguna web.</p>
                        ) : (
                            vistos.data.map((v) => (
                                <div key={v.domain} className="rv-wb-cambio">
                                    <span className="rv-ca-baldosa">
                                        <Icono nombre={v.allowed ? 'circle-check' : 'ban'} />
                                    </span>
                                    <span className="rv-ca-fila__texto">
                                        <strong>{v.domain}</strong>
                                        <span>{`${v.allowed ? 'Permitida' : 'No permitida'} · visto por última vez ${diaYHora(v.last_seen_at)}${v.last_block_reason == null ? '' : ` · ${MOTIVO_BLOQUEO[v.last_block_reason]}`}`}</span>
                                    </span>
                                </div>
                            ))
                        ))}
                </div>
            </PanelLateral>
            <Ventana
                abierta={borrar}
                titulo={`Borrar «${w.name}»`}
                texto="Dejará de verse en todas las webs donde lo pegaste y su código no vuelve a servir."
                cancelar="Cancelar"
                accion="Borrar"
                accionIcono="trash-2"
                ocupado={ocupado === 'borrar'}
                movil={movil}
                onCerrar={() => { setBorrar(false); }}
                onAccion={() => {
                    setOcupado('borrar');
                    ops.deleteWidget(negocio, w.id)
                        .then(() => {
                            avisar({ tipo: 'ok', texto: 'Widget borrado.' });
                            setBorrar(false);
                            onCerrar();
                            onCambiado();
                        })
                        .catch((e: unknown) => { avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido borrar.' }); })
                        .finally(() => { setOcupado(null); });
                }}
            />
        </>
    );
}

function Nuevo({ ops, negocio, movil, abierto, tipos, onCerrar, onCreado }: PropsPantalla & { abierto: boolean; tipos: readonly TipoWidget[]; onCerrar: () => void; onCreado: (w: Widget) => void }) {
    const avisar = useAvisoBreve();
    const [codigo, setCodigo] = useState('');
    const [nombre, setNombre] = useState('');
    const [opciones, setOpciones] = useState<Record<string, unknown>>({});
    const [tema, setTema] = useState<Tema>('light');
    const [acento, setAcento] = useState(ACENTO_INICIAL);
    const [dominios, setDominios] = useState('');
    const [ocupado, setOcupado] = useState<'borrador' | 'activar' | null>(null);

    const [antes, setAntes] = useState(abierto);
    if (antes !== abierto) {
        setAntes(abierto);
        if (abierto) {
            setCodigo(tipos[0]?.code ?? '');
            setNombre('');
            setOpciones({});
            setTema('light');
            setAcento(ACENTO_INICIAL);
            setDominios('');
        }
    }

    const tipo = tipos.find((t) => t.code === codigo);
    const lista = dominiosDe(dominios);
    const malo = tipo === undefined || nombre.trim() === '' || !COLOR.test(acento) || lista.length > 10;

    const crear = (activar: boolean) => {
        if (tipo === undefined) {
            return;
        }
        setOcupado(activar ? 'activar' : 'borrador');
        ops.createWidget(negocio, { name: nombre.trim(), type_code: tipo.code, options: opciones as Record<string, never>, theme: tema, accent_color: acento, domains: lista })
            .then((w) => (activar ? ops.activateWidget(negocio, w.id) : w))
            .then(onCreado)
            .catch((e: unknown) => { avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido crear el widget.' }); })
            .finally(() => { setOcupado(null); });
    };

    return (
        <PanelLateral abierto={abierto} onCerrar={onCerrar} movil={movil} titulo="Nuevo widget" eyebrow="Widgets" icono="code" accion="Guardar como borrador" ocupado={ocupado === 'borrador'} inactiva={malo} onAccion={() => { crear(false); }} cerrar="Cancelar">
            <div className="rv-wb-form">
                <p className="rv-wb-provisional">{CATALOGO_PROVISIONAL}</p>
                <Desplegable etiqueta="Qué hace" valor={codigo} opciones={tipos.map((t) => ({ valor: t.code, texto: t.entitlement_active ? t.name : `${t.name} · No contratado` }))} onChange={(v) => { setCodigo(v); setOpciones({}); }} ayuda={tipo === undefined ? null : tipo.entitlement_active ? (tipo.description ?? null) : 'Sin contratar se ve plegado y no se puede activar.'} />
                <Campo etiqueta="Nombre del widget" valor={nombre} max={80} ayuda="Solo lo ve tu equipo." onChange={setNombre} />
                {tipo !== undefined && hayOpciones(tipo.options_schema) && <EditorOpciones esquema={tipo.options_schema} valor={opciones} onChange={setOpciones} />}
                <Segmentos etiqueta="Tema" items={TEMAS.map((t) => ({ etiqueta: t.texto, icono: t.icono }))} valor={Math.max(0, TEMAS.findIndex((t) => t.valor === tema))} onCambio={(i) => { setTema(TEMAS[i]?.valor ?? 'light'); }} />
                <div className="rv-wb-color">
                    <i style={{ background: COLOR.test(acento) ? acento : 'transparent' }} aria-hidden="true" />
                    <Campo etiqueta="Color de acento" valor={acento} max={7} error={COLOR.test(acento) ? null : 'Escríbelo así: #DD1420'} onChange={(v) => { setAcento(v.trim()); }} />
                </div>
                <Campo etiqueta="Webs donde lo pegas" filas={3} valor={dominios} onChange={setDominios} ayuda="Una por línea, sin https://. Hasta 10." error={lista.length > 10 ? 'Como mucho 10 webs.' : null} />
                <div>
                    <Boton icono="play" disabled={malo || lista.length === 0 || !tipo.entitlement_active} ocupado={ocupado === 'activar'} onClick={() => { crear(true); }}>
                        Guardar y activar
                    </Boton>
                </div>
            </div>
        </PanelLateral>
    );
}
